<script>
  // Примитив нижней шторки (bottom-sheet): фиксированный оверлей + панель снизу.
  // Закрытие: тап по затемнению, Escape. Фокус переводится в панель и
  // возвращается на прежний элемент после закрытия; Tab зациклен внутри.
  import { createEventDispatcher, tick } from 'svelte';

  export let open = false;
  export let title = '';

  const dispatch = createEventDispatcher();

  let panelElement = null;
  let previouslyFocused = null;

  $: if (open) {
    focusPanel();
  } else {
    restoreFocus();
  }

  async function focusPanel() {
    previouslyFocused = typeof document !== 'undefined' ? document.activeElement : null;
    await tick();
    panelElement?.focus();
  }

  function restoreFocus() {
    if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
      previouslyFocused.focus();
    }
    previouslyFocused = null;
  }

  function close() {
    dispatch('close');
  }

  // Запасной Escape (1.26.5): фокус уходит из панели, когда нажатая кнопка на время
  // запроса становится disabled, — тогда keydown панели не срабатывал и шторка не
  // закрывалась. Панель гасит своё событие (stopPropagation), двойного закрытия нет.
  function handleWindowKeydown(event) {
    if (open && event.key === 'Escape' && !event.defaultPrevented) {
      close();
    }
  }

  function handleKeydown(event) {
    if (event.key === 'Escape') {
      event.stopPropagation();
      close();
      return;
    }
    if (event.key !== 'Tab' || !panelElement) {
      return;
    }
    // Простая ловушка фокуса: Tab с последнего элемента — на первый и наоборот.
    const focusable = panelElement.querySelectorAll(
      'button:not([disabled]), input, textarea, select, a[href], [tabindex]:not([tabindex="-1"])'
    );
    if (!focusable.length) {
      event.preventDefault();
      return;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }
</script>

<svelte:window on:keydown={handleWindowKeydown} />

{#if open}
  <!-- svelte-ignore a11y-click-events-have-key-events a11y-no-static-element-interactions -->
  <div class="sheet-backdrop" on:click={close}></div>
  <div class="sheet-dock">
    <div
      class="sheet-panel"
      bind:this={panelElement}
      role="dialog"
      aria-modal="true"
      aria-label={title || 'Действия'}
      tabindex="-1"
      on:keydown={handleKeydown}
    >
      <div class="sheet-grabber" aria-hidden="true"></div>
      {#if title}
        <div class="sheet-title">{title}</div>
      {/if}
      <slot />
    </div>
  </div>
{/if}

<style>
  .sheet-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 300;
  }

  /* В10 (1.27.2): док высотой с видимую область (--app-height из viewport.js, как у
     редактора правки). Клавиатура iOS не сжимает layout-вьюпорт, и шторка, прижатая
     к низу экрана, прятала поле и кнопку «Сохранить» под клавиатурой. */
  .sheet-dock {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    height: var(--app-height, 100dvh);
    z-index: 301;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    pointer-events: none;
  }

  .sheet-panel {
    position: relative;
    pointer-events: auto;
    max-height: min(75vh, calc(var(--app-height, 100dvh) - 2rem));
    overflow-y: auto;
    overscroll-behavior: contain;
    background: var(--bg-secondary);
    border-radius: 16px 16px 0 0;
    padding: 0.5rem 1rem calc(1.5rem + env(safe-area-inset-bottom, 0px));
    box-shadow: var(--shadow-lg);
    outline: none;
    /* Пункты шторки — кнопки, не текст: без этого шторка, открытая удержанием,
       оказывалась под ещё лежащим пальцем, и iOS начинала выделять текст пункта
       (замечание владельца 2026-09-22). Полям ввода выделение возвращается ниже. */
    -webkit-user-select: none;
    user-select: none;
    -webkit-touch-callout: none;
  }

  .sheet-panel :global(input),
  .sheet-panel :global(textarea) {
    -webkit-user-select: text;
    user-select: text;
  }

  .sheet-grabber {
    width: 36px;
    height: 4px;
    border-radius: 2px;
    background: var(--border-color);
    margin: 0.25rem auto 0.65rem;
  }

  .sheet-title {
    font-size: 0.8125rem;
    color: var(--text-muted);
    padding: 0 0.5rem 0.65rem;
    border-bottom: 1px solid var(--border-light);
    margin-bottom: 0.25rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
</style>
